import type {EmptyStateProps} from 'shells/card/card-widget/card-widget';
import {SearchRegularIcon} from 'shells/icon/svg/search-regular-icon';
import {WarningOutlineIcon} from 'shells/icon/svg/warning-outline-icon';
import {Illustration} from 'shells/illustrations/illustration';

import {LeadResearchSentiment} from 'nutshell-graphql-types';
import type {LeadResearchSummaryFragment as LeadResearchSummary} from 'nutshell-graphql-types';

/**
 * Normalizes a GraphQL LeadResearchSentiment enum value to a lowercase CSS class name.
 * Used for applying sentiment-specific styling (e.g., good/bad/neutral colors).
 */
export function normalizeSentimentToClass(
    sentiment: LeadResearchSentiment
): 'good' | 'bad' | 'neutral' {
    switch (sentiment) {
        case LeadResearchSentiment.GOOD:
            return 'good';
        case LeadResearchSentiment.BAD:
            return 'bad';
        case LeadResearchSentiment.NEUTRAL:
        default:
            return 'neutral';
    }
}

export function getLeadResearchEmptyStateProps(
    research: LeadResearchSummary | null | undefined,
    canResearch: boolean,
    error: string | null | undefined,
    hasUsedLeadResearcherBefore: boolean
): EmptyStateProps | undefined {
    if (error && !research) {
        return {
            isEmpty: true,
            text: 'We ran into a hiccup while researching this lead.\nPlease try again in a moment or contact support.',
            icon: WarningOutlineIcon,
        };
    }

    if (!canResearch) {
        return {
            isEmpty: true,
            text: 'Add a business email or company\nwebsite to research this lead',
            icon: SearchRegularIcon,
        };
    }

    if (!research && hasUsedLeadResearcherBefore) {
        return {
            isEmpty: true,
            text: 'Research this lead with AI to gain better\ncompany insights and identify the best fit.',
            illustration: <Illustration src='lead-researcher-empty-state' width={72} />,
        };
    }

    return undefined;
}
